import React, { useEffect } from 'react';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';
import { setPagination } from '../../redux/Actions/TableAction';
import AppTable from '../../components/AppTable/AppTable';
import { searchCoupon, deleteCoupon } from '../../constant/Constant';
import toast from 'react-hot-toast';
import DeleteButton from '../../components/Delete/DeleteButton';
import EditButton from '../../components/Edit/EditButton';
import { Badge } from 'lucide-react';

function CouponMaster() {
  const dispatch = useDispatch();
  const ApiReducer = useSelector((state) => state.ApiReducer);
  const TableDataReducer = useSelector((state) => state.TableReducer);

  useEffect(() => {
    fetchCoupon();
  }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit]);

  const fetchCoupon = () => {
    const json = {
      page: TableDataReducer.pagination.page,
      limit: TableDataReducer.pagination.limit,
      search: {}
    };

    HitApi(json, searchCoupon).then((res) => {
      console.log("Coupon API Response:", res);
      
      if (res?.statusCode === 200 && res?.data) {
        const couponData = res.data.docs || [];

        dispatch(setApiJson(couponData));
        dispatch(setPagination({
          total: res.data.totalDocs || 0,
          page: res.data.page || 1,
          limit: res.data.limit || 10,
          totalPages: res.data.totalPages || 1,
          hasNextPage: res.data.hasNextPage || false,
          hasPrevPage: res.data.hasPrevPage || false
        }));
      } else {
        dispatch(setApiJson([]));
        dispatch(setPagination({
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 1
        }));
      }
    }).catch(error => {
      console.error("Error loading coupons:", error);
      dispatch(setApiJson([]));
      toast.error('Failed to load coupons');
    });
  };

  const handleEdit = (row) => {
    console.log("Edit coupon:", row);
    window.location.href = `/coupon/edit/${row._id}`;
  };

  const handleDelete = (row) => {
    if (window.confirm(`Are you sure you want to delete coupon "${row.code}"?`)) {
      HitApi({ _id: row._id }, deleteCoupon).then((res) => {
        if (res?.statusCode === 200) {
          toast.success('Coupon deleted successfully');
          fetchCoupon();
        } else {
          toast.error(res?.message || 'Failed to delete coupon');
        }
      }).catch(error => {
        console.error("Error deleting coupon:", error);
        toast.error('Failed to delete coupon');
      });
    }
  };

  const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

  // Format timestamp to readable date with time
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
  };

  // Format timestamp to readable date and time
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const dateStr = date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return `${dateStr} ${timeStr}`;
  };

  // Format currency in Indian Rupees
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '₹0';
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  // Get coupon status based on validity and active state
  const getCouponStatus = (startTimestamp, endTimestamp, isActive) => {
    const now = Date.now();
    
    if (!isActive) {
      return { label: 'Inactive', color: 'bg-red-100 text-red-800' };
    }
    
    if (now < startTimestamp) {
      return { label: 'Scheduled', color: 'bg-blue-100 text-blue-800' };
    }
    
    if (now > endTimestamp) {
      return { label: 'Expired', color: 'bg-gray-100 text-gray-800' };
    }
    
    const daysLeft = Math.ceil((endTimestamp - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft <= 3) {
      return { label: `Expires in ${daysLeft}d`, color: 'bg-yellow-100 text-yellow-800' };
    }
    
    return { label: 'Active', color: 'bg-green-100 text-green-800' };
  };

  const tableHeaders = [
    {
      title: "S.No",
      key: "serialNumber",
      width: "60px",
      align: "center",
      priority: 1,
      render: (_, row, index) => (
        <div className="text-center">
          <span className="text-sm font-medium text-gray-900">
            {baseIndex + index + 1}
          </span>
        </div>
      )
    },
    {
      title: "Coupon Code",
      key: "code",
      priority: 1,
      render: (value) => (
        <div className="font-mono font-bold text-indigo-600 bg-indigo-50 px-3 py-1.5 rounded-md inline-block uppercase">
          {value || 'N/A'}
        </div>
      )
    },
    {
      title: "Coupon Details",
      key: "details",
      priority: 1,
      render: (_, row) => (
        <div className="flex flex-col">
          <div className="font-semibold text-gray-900 text-sm">{row.name || 'N/A'}</div>
          <div className="text-xs text-gray-500 mt-0.5 line-clamp-2" title={row.description}>
            {row.description || 'No description'}
          </div>
        </div>
      )
    },
    {
      title: "Discount",
      key: "discount",
      align: "start",
      priority: 1,
      render: (_, row) => (
        <div className="flex flex-col gap-1">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold w-fit ${
            row.discountType === 'percentage' ? 'bg-green-100 text-green-800' :
            row.discountType === 'fixed' ? 'bg-blue-100 text-blue-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {row.discountType === 'percentage' 
              ? `${row.discountValue}% OFF` 
              : row.discountType === 'fixed'
              ? `${formatCurrency(row.discountValue)} OFF`
              : 'N/A'}
          </span>
          {row.discountType === 'percentage' && row.maxDiscountAmount && (
            <span className="text-xs text-gray-500 font-medium">
              Max: {formatCurrency(row.maxDiscountAmount)}
            </span>
          )}
        </div>
      )
    },
    {
      title: "Applicable On",
      key: "applicableOn",
      align: "start",
      priority: 2,
      render: (value) => (
        <span className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap ${
          value === 'both' ? 'bg-purple-100 text-purple-800' :
          value === 'service' ? 'bg-orange-100 text-orange-800' :
          value === 'product' ? 'bg-teal-100 text-teal-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {value === 'both' ? 'Services & Products' :
           value === 'service' ? 'Services Only' :
           value === 'product' ? 'Products Only' :
           value || 'N/A'}
        </span>
      )
    },
    {
      title: "Conditions",
      key: "conditions",
      align: "start",
      priority: 3,
      render: (_, row) => (
        <div className="text-xs text-gray-600 space-y-1">
          <div className="flex items-center gap-1">
            <span className="font-medium text-gray-700">Min:</span>
            <span>{row.minPurchaseAmount > 0 ? formatCurrency(row.minPurchaseAmount) : 'None'}</span>
          </div>
        </div>
      )
    },
    {
      title: "Usage Limits",
      key: "usage",
      align: "start",
      priority: 2,
      render: (_, row) => (
        <div className="flex flex-col text-xs space-y-1">
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Total:</span>
            <span className="font-semibold text-gray-900">{row.usedCount || 0}</span>
            <span className="text-gray-400">/</span>
            <span className="text-gray-700">{row.maxUsageLimit || '∞'}</span>
          </div>
          {row.perUserLimit && (
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Per User:</span>
              <span className="text-gray-700 font-medium">{row.perUserLimit}</span>
            </div>
          )}
        </div>
      )
    },
    {
      title: "Valid Period",
      key: "validity",
      priority: 2,
      render: (_, row) => (
        <div className="text-xs space-y-1">
          <div className="flex items-center gap-1">
            <span className="text-gray-500 font-medium">From:</span>
            <span className="text-gray-700">{formatDate(row.startTimestamp)}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500 font-medium">To:</span>
            <span className="text-gray-700">{formatDate(row.endTimestamp)}</span>
          </div>
        </div>
      )
    },
    {
      title: "Status",
      key: "status",
      align: "start",
      priority: 1,
      render: (_, row) => {
        const status = getCouponStatus(row.startTimestamp, row.endTimestamp, row.isActive);
        return (
          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${status.color}`}>
            {status.label}
          </span>
        );
      }
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: "120px",
      priority: 1,
      render: (value, row) => (
        <div className="flex justify-center items-center space-x-2">
          <EditButton
            onEdit={() => handleEdit(row)}
            itemName="coupon"
          />
          <DeleteButton
            onDelete={() => handleDelete(row)}
            itemName="coupon"
          />
        </div>
      )
    }
  ];

  const tableData = Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : [];
  const isLoading = ApiReducer.apiJson === null || ApiReducer.apiJson === undefined;

  return (
    <div className="p-5">
      <PageHeader
        title="Coupon Management"
        description="View, create, and manage discount coupons and promotional offers"
      />
      <AppTable
        TH={tableHeaders}
        TD={tableData}
        isLoading={isLoading}
        emptyMessage="No coupons found. Create your first coupon to get started!"
        buttonText="Add Coupon"
        navigateTo="/coupon/add"
      />
    </div>
  );
}

export default CouponMaster;